/**
 * useAppRole — BeHeard app role profile hook
 *
 * Resolves the current user's BeHeard role profile(s) from Central role + scopes.
 * This is for DISPLAY ONLY — it does NOT control access.
 *
 * All authorization decisions still use useCentralConsumer().hasCapability().
 */

import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  selectBootCurrentWorkspace,
  selectBootCompleted,
} from '../store/slices/bootContextSlice';
import {
  resolveAppRoles,
  APP_ROLE_PROFILES,
  SCOPE_BUNDLES,
  type CentralRole,
  type ResolvedAppRole,
  type AppRoleProfile,
  type AppRoleKey,
} from '../../../shared/src/appRoles';

export type { ResolvedAppRole, AppRoleProfile, AppRoleKey };
export { APP_ROLE_PROFILES, SCOPE_BUNDLES };

/**
 * Returns the resolved BeHeard app role profile for the current user.
 * Returns null if boot is not complete.
 */
export function useAppRole(): ResolvedAppRole | null {
  const bootCompleted = useSelector(selectBootCompleted);
  const workspace = useSelector(selectBootCurrentWorkspace);

  return useMemo(() => {
    if (!bootCompleted || !workspace) return null;

    const centralRole = (workspace.role ?? 'member') as CentralRole;
    const scopes = workspace.scopes ?? [];

    return resolveAppRoles(centralRole, scopes);
  }, [bootCompleted, workspace]);
}
